import ActionResult from 'app/src/engine/action-results/ActionResult';
import InstructionSegment, {
	InstructionSegmentStatus,
} from 'app/src/engine/instructions/InstructionSegment';
import MovableItem, { MovableItemLocation } from 'app/src/engine/movable-items/MovableItem';
import GameStateStore from 'app/src/game/GameStateStore';

export default class Instruction {
	// When comparing instructions, the higher priority will go first and ties
	// will be broken by the instance priority.
	get basePriority(): number {
		throw new Error('Subclasses must override.');
	}

	// Pick a random priority for this instruction.
	protected _instancePriority = Math.floor(Math.random() * 100000);
	get instancePriority(): number {
		return this._instancePriority;
	}

	get finishedExecuting(): boolean {
		return this._remainingSegments.length === 0;
	}

	protected _remainingSegments: InstructionSegment[] = [];

	// Interactions need to be reset after being used.
	//
	// Subclasses should override.
	public reset(): void {
		// Virtual.
	}

	// Execute this action instruction causing any movements or transformations
	// to the item. Normally, only one MovementResult will be returned, but
	// it's possible that a robot pushes another in which an action result
	// group will be returned.
	public executeSegment(gameStateStore: GameStateStore, movableItem: MovableItem): ActionResult[] {
		if (!this._remainingSegments.length) {
			throw new Error('No remaining instruction segments to execute');
		}

		// Get the current segment.
		const currentSegment = this._remainingSegments.splice(0, 1)[0];

		// Ask tile to transform segment.
		const movableItemLocation = gameStateStore.movableItemLocations.get(
			movableItem,
		) as MovableItemLocation;
		const currentTile = gameStateStore.getTile(movableItemLocation.row, movableItemLocation.col);
		const transformedSegment = currentTile.transformInstructionSegment(currentSegment);

		// Fetch action results and status.
		const segmentResults = transformedSegment.getActionResults(gameStateStore, movableItem);

		// Start a movement transaction. We may be moving multiple robots.
		gameStateStore.startMovementTransaction();

		// Execute the action results.
		for (const segmentActionResult of segmentResults.actionResults) {
			segmentActionResult.execute(gameStateStore);
		}

		// We're done moving robots. Commit the transaction.
		gameStateStore.finishMovementTransaction();

		// If this segment's action is set to end the instruction, remove all other segments.
		if (segmentResults.status === InstructionSegmentStatus.CancelFutureSegments) {
			this._remainingSegments.splice(0, this._remainingSegments.length);
		}

		return segmentResults.actionResults;
	}
}
